import { LANDING_FACTS_TIMEOUT_MS } from '~/constants/landing';
import {
  EMPTY_LANDING_FACTS,
  pricesFrom,
  topSelfClosePercentFrom,
  totalProjectsFrom,
  type LandingFacts,
} from '~/utils/landing/facts';

const LANDING_FACTS_KEY = 'landing-facts';

const fetchData = async (url: string): Promise<unknown> => {
  const res = await $fetch<{ data?: unknown }>(url, { timeout: LANDING_FACTS_TIMEOUT_MS });
  return res?.data;
};

/**
 * Live numbers on the landing pages, rendered on the server: top self-close split (tier config),
 * catalogue project count and plan prices. A failing source yields null, never a made-up number.
 */
export const useLandingFacts = () => {
  const { data } = useAsyncData<LandingFacts>(
    LANDING_FACTS_KEY,
    async () => {
      const [tiers, developers, prices] = await Promise.allSettled([
        fetchData('/api/agent/commission-tiers'),
        fetchData('/api/developers'),
        fetchData('/api/billing/prices'),
      ]);
      const value = <T>(result: PromiseSettledResult<unknown>, pick: (data: unknown) => T | null): T | null =>
        result.status === 'fulfilled' ? pick(result.value) : null;
      return {
        topSelfClosePercent: value(tiers, topSelfClosePercentFrom),
        totalProjects: value(developers, totalProjectsFrom),
        prices: value(prices, pricesFrom),
      };
    },
    { default: () => EMPTY_LANDING_FACTS },
  );

  const facts = computed(() => data.value ?? EMPTY_LANDING_FACTS);

  return {
    facts,
    topSelfClosePercent: computed(() => facts.value.topSelfClosePercent),
    totalProjects: computed(() => facts.value.totalProjects),
    prices: computed(() => facts.value.prices),
  };
};
